import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db, functions } from '../lib/firebase';
import {
  CALLABLES,
  COLLECTIONS,
  isHalfHoliday,
  targetMinutesForDay,
  resolveWorkTimeEntry,
  absenceFractionForDate,
  computeWorkedMinutes,
  computeBreakMinutes,
  entrySessions,
  eachDate,
  type WeeklyHoursEntry,
  type TimeClockInput,
  type TimeClockResponse,
  type TimeUpsertDayInput,
  type TimeUpsertDayResponse,
  type TimeSetDefaultHoursInput,
  type TimeSetDefaultHoursResponse,
  type TimeSubmitStatementInput,
  type TimeRecallStatementInput,
  type TimeDecideStatementInput,
  type TimeStatementActionResponse,
  type TimeTeamOverviewRequest,
  type TimeTeamOverviewResponse,
  type TimeMemberMonthRequest,
  type TimeMemberMonthResponse,
  type TimeEntry,
  type TimeStatement,
  type AbsenceRequest,
} from '@daaily/shared';

export const callTimeClock = httpsCallable<TimeClockInput, TimeClockResponse>(
  functions,
  CALLABLES.TIME_CLOCK,
);
export const callTimeUpsertDay = httpsCallable<TimeUpsertDayInput, TimeUpsertDayResponse>(
  functions,
  CALLABLES.TIME_UPSERT_DAY,
);
export const callTimeSetDefaultHours = httpsCallable<TimeSetDefaultHoursInput, TimeSetDefaultHoursResponse>(
  functions,
  CALLABLES.TIME_SET_DEFAULT_HOURS,
);
export const callTimeSubmitStatement = httpsCallable<
  TimeSubmitStatementInput,
  TimeStatementActionResponse
>(functions, CALLABLES.TIME_SUBMIT_STATEMENT);
export const callTimeRecallStatement = httpsCallable<
  TimeRecallStatementInput,
  TimeStatementActionResponse
>(functions, CALLABLES.TIME_RECALL_STATEMENT);
export const callTimeDecideStatement = httpsCallable<
  TimeDecideStatementInput,
  TimeStatementActionResponse
>(functions, CALLABLES.TIME_DECIDE_STATEMENT);
export const callTimeTeamOverview = httpsCallable<TimeTeamOverviewRequest, TimeTeamOverviewResponse>(
  functions,
  CALLABLES.TIME_TEAM_OVERVIEW,
);
export const callTimeMemberMonth = httpsCallable<TimeMemberMonthRequest, TimeMemberMonthResponse>(
  functions,
  CALLABLES.TIME_MEMBER_MONTH,
);

/**
 * Live-query result with a first-load flag. `loading` is true until the first
 * snapshot (or error) for the CURRENT email arrives, so pages can show
 * <Loading /> instead of a false empty state. The plain hooks below
 * (useMyTimeStatements etc.) keep returning just the data for callers that
 * don't care (header clock, badges).
 */
export interface LiveResult<T> {
  data: T;
  loading: boolean;
}

/**
 * Tracks which email the latest snapshot belongs to. `loading` is derived, so
 * switching email flips straight back to loading without an extra render.
 */
function useLoadedFor(email: string | null) {
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const loading = email !== null && loadedFor !== email;
  return { loading, markLoaded: setLoadedFor };
}

export function useMyTimeStatementsState(email: string | null): LiveResult<Map<string, TimeStatement>> {
  const [map, setMap] = useState<Map<string, TimeStatement>>(new Map());
  const { loading, markLoaded } = useLoadedFor(email);
  useEffect(() => {
    if (!email) {
      setMap(new Map());
      return;
    }
    const q = query(
      collection(db, COLLECTIONS.TIME_STATEMENTS),
      where('userEmail', '==', email.toLowerCase()),
    );
    return onSnapshot(
      q,
      (snap) => {
        const m = new Map<string, TimeStatement>();
        snap.forEach((d) => {
          const s = d.data() as TimeStatement;
          m.set(s.period, s);
        });
        setMap(m);
        markLoaded(email);
      },
      () => {
        setMap(new Map());
        markLoaded(email);
      },
    );
  }, [email, markLoaded]);
  return { data: map, loading };
}

/** Live map of the user's own monthly statements, keyed by period (YYYY-MM). */
export function useMyTimeStatements(email: string | null): Map<string, TimeStatement> {
  return useMyTimeStatementsState(email).data;
}

/** Time sheets submitted to me for approval (all statuses), with a first-load flag. */
export function usePendingTimeApprovalsState(email: string | null): LiveResult<TimeStatement[]> {
  const [list, setList] = useState<TimeStatement[]>([]);
  const { loading, markLoaded } = useLoadedFor(email);
  useEffect(() => {
    if (!email) {
      setList([]);
      return;
    }
    const q = query(
      collection(db, COLLECTIONS.TIME_STATEMENTS),
      where('approverEmail', '==', email.toLowerCase()),
    );
    return onSnapshot(
      q,
      // ALL statuses — the approvals view filters (Open/Approved/Rejected chips).
      (snap) => {
        setList(snap.docs.map((d) => d.data() as TimeStatement));
        markLoaded(email);
      },
      () => {
        setList([]);
        markLoaded(email);
      },
    );
  }, [email, markLoaded]);
  return { data: list, loading };
}

/** Time sheets submitted to me for approval (status submitted). */
export function usePendingTimeApprovals(email: string | null): TimeStatement[] {
  return usePendingTimeApprovalsState(email).data;
}

/** Live map of the user's time entries keyed by date, with a first-load flag. */
export function useMyTimeEntriesState(email: string | null): LiveResult<Map<string, TimeEntry>> {
  const [map, setMap] = useState<Map<string, TimeEntry>>(new Map());
  const { loading, markLoaded } = useLoadedFor(email);
  useEffect(() => {
    if (!email) {
      setMap(new Map());
      return;
    }
    const q = query(
      collection(db, COLLECTIONS.TIME_ENTRIES),
      where('userEmail', '==', email.toLowerCase()),
    );
    return onSnapshot(
      q,
      (snap) => {
        const m = new Map<string, TimeEntry>();
        snap.forEach((d) => {
          const e = d.data() as TimeEntry;
          m.set(e.date, e);
        });
        setMap(m);
        markLoaded(email);
      },
      () => {
        setMap(new Map());
        markLoaded(email);
      },
    );
  }, [email, markLoaded]);
  return { data: map, loading };
}

/** Live map of the user's time entries, keyed by date (YYYY-MM-DD). */
export function useMyTimeEntries(email: string | null): Map<string, TimeEntry> {
  return useMyTimeEntriesState(email).data;
}

export function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Worked minutes for an entry across all sessions, counting an open session live. */
export function workedMinutesLive(entry: TimeEntry | undefined, nowMs: number): number {
  return entry ? computeWorkedMinutes(entry, nowMs) : 0;
}

/** Break minutes for an entry across all sessions, counting an open break live. */
export function breakMinutesLive(entry: TimeEntry | undefined, nowMs: number): number {
  return entry ? computeBreakMinutes(entry, nowMs) : 0;
}

/** Is the user currently checked in (latest session still open)? on break?
 *  `checkedOut` = at least one session exists and none is open. */
export function clockState(entry: TimeEntry | undefined): {
  checkedIn: boolean;
  onBreak: boolean;
  checkedOut: boolean;
} {
  const sessions = entry ? entrySessions(entry) : [];
  const cur = sessions.length > 0 ? sessions[sessions.length - 1]! : null;
  const checkedIn = !!cur && cur.checkOutAt == null;
  const onBreak =
    checkedIn && cur!.breaks.length > 0 && cur!.breaks[cur!.breaks.length - 1]!.end == null;
  return { checkedIn, onBreak, checkedOut: sessions.length > 0 && !checkedIn };
}

/** Minimal shape for resolving the effective work-time arrangement per date. */
export type WorkTimeUser = {
  weeklyHoursHistory?: WeeklyHoursEntry[] | null;
  weeklyWorkingHours?: number | null;
  employmentStartDate?: string | null;
};

export function targetForDay(
  date: string,
  weeklyHours: number | null | undefined,
  holidays: Set<string>,
  absences: AbsenceRequest[],
  workTimeUser?: WorkTimeUser | null,
): number {
  return targetMinutesForDay({
    date,
    weeklyHours,
    isHoliday: holidays.has(date),
    isHalfHoliday: isHalfHoliday(holidays, date),
    absenceFraction: absenceFractionForDate(date, absences),
    workTime: workTimeUser ? resolveWorkTimeEntry(workTimeUser, date) : null,
  });
}

/**
 * YTD hour-status minutes = openingBalance + Σ(worked − target) from startDate
 * to today (every working day counts; missing days are a full deficit).
 */
export function computeStatusMinutes(args: {
  startDate: string | null;
  openingBalanceHours: number | null;
  today: string;
  entries: Map<string, TimeEntry>;
  weeklyHours: number | null;
  holidays: Set<string>;
  absences: AbsenceRequest[];
  /** Overtime-compensation minutes (approved + submitted) to subtract. */
  overtimeCompMinutes?: number;
  /** Work-time arrangement → per-day (part-time) targets. */
  workTimeUser?: WorkTimeUser | null;
  nowMs: number;
}): number {
  let total = Math.round((args.openingBalanceHours ?? 0) * 60);
  if (!args.startDate) return total;
  for (const date of eachDate(args.startDate, args.today)) {
    const target = targetForDay(date, args.weeklyHours, args.holidays, args.absences, args.workTimeUser);
    const worked = workedMinutesLive(args.entries.get(date), args.nowMs);
    total += worked - target;
  }
  return total - (args.overtimeCompMinutes ?? 0);
}
